import { DYNAMICS_FIELDS, DYNAMICS_FORM_API_URL } from "@constants";
import {
    type FormalizrDynamicsFormData,
    type FormalizrDynamicsMetaData,
    type FormalizrLocale,
    type DynamicsBool,
    type DynamicsMap,
    type FormMapperApp,
    type FormMapperAppData,
} from "@types";
import { decodeBase64ToObject, encodeObjectToBase64 } from "@utils/codec";
import { getCurrentDateInOneSiteFormat } from "@utils/date";

export const addValueToEncodedField = (
    form: HTMLFormElement,
    key: string,
    value: string,
): void => {
    const field = form.querySelector(
        `input[name="${DYNAMICS_FIELDS.formData}"]`,
    ) as HTMLInputElement;

    if (field.value === "") {
        field.value = encodeObjectToBase64({ [key]: value });
        return;
    }

    const decodedValue = decodeBase64ToObject(
        field.value,
    ) as FormalizrDynamicsFormData;

    const newValue = {
        ...decodedValue,
        [key]: value,
    };

    field.value = encodeObjectToBase64(newValue);
};

export const fillDynamicsMetaData = (
    form: HTMLFormElement,
    locale: FormalizrLocale,
): void => {
    const field = form.querySelector(
        `input[name="${DYNAMICS_FIELDS.metaData}"]`,
    ) as HTMLInputElement;

    const decodedValue = decodeBase64ToObject(
        field.value,
    ) as FormalizrDynamicsMetaData;

    const newValue: FormalizrDynamicsMetaData = {
        ...decodedValue,
        timeOfSumbit: getCurrentDateInOneSiteFormat(locale),
        // pageOfEntry: sessionPageTrace.entry.path ?? decodedValue.currentPage,
    };

    field.value = encodeObjectToBase64(newValue);
};

export const createMappingsFromMappingData = (
    mappingData: Exclude<FormMapperApp, null | undefined>,
): DynamicsMap[] => {
    return Object.entries(mappingData.data).map(([formKey, newMapping]) => {
        if (newMapping.ignore || !newMapping.alias) {
            return {
                DataverseFieldName: formKey,
                FormFieldName: formKey,
            };
        }

        const mapping: DynamicsMap = {
            DataverseFieldName: newMapping.alias,
            FormFieldName: formKey,
        };

        //Checkbox
        if (newMapping.type === "boolean") {
            return {
                ...mapping,
                DataverseFieldValue: newMapping.enum?.true ?? "",
            };
        }

        //Checkbox group
        if (newMapping.enum) {
            return {
                ...mapping,
                DataverseFieldValue: Object.entries(newMapping.enum).map(
                    ([enumKey, enumValue]) => ({
                        DataverseValue: enumValue,
                        FormValue: enumKey,
                    }),
                ),
            };
        }

        return mapping;
    });
};

export const mapBooleanToDynamicsBool = (bool: boolean): DynamicsBool => {
    return bool ? "1" : "0";
};

export const fillDynamicsForm = (
    formName: string,
    fieldName: string,
    value: string | boolean,
): void => {
    try {
        const dynamicsForm = document.querySelector(
            `form[name="${formName}"]`,
        ) as HTMLFormElement;

        if (!dynamicsForm) {
            throw new Error(`Dynamics form not found - formName: ${formName}`);
        }

        const input = dynamicsForm.querySelector(
            `input[name="${fieldName}"]`,
        ) as HTMLInputElement;

        if (!input) {
            throw new Error(
                `Input field not found - formName: ${formName}, fieldName: ${fieldName}`,
            );
        }

        const processedValue =
            typeof value === "boolean"
                ? mapBooleanToDynamicsBool(value)
                : value;

        if (typeof value === "boolean") {
            input.checked = value;
        } else {
            input.value = value;
        }

        addValueToEncodedField(dynamicsForm, fieldName, processedValue);
    } catch (error) {
        console.error(error);
    }
};

export const submitDynamicsForm = async (
    formName: string,
    locale: FormalizrLocale,
    formMapperAppData: FormMapperAppData,
): Promise<void> => {
    try {
        if ("d365mktformcapture" in window === false) {
            throw new Error(
                "d365mktformcapture not found on window, verify Dynamics script is loaded",
            );
        }
        if (!formMapperAppData.formMapper) {
            throw new Error(
                "Form mapper app values not found, verify the app is installed",
            );
        }

        // Since this global is added on the fly by the Dynamics script, we cannot import it or its types through a package
        const d365mktformcapture = (window as any).d365mktformcapture;

        const dynamicsForm = document.querySelector(
            `form[name="${formName}"]`,
        ) as HTMLFormElement;

        if (!dynamicsForm) {
            throw new Error(`Dynamics form not found - formName: ${formName}`);
        }

        fillDynamicsMetaData(dynamicsForm, locale);

        dynamicsForm.addEventListener("submit", event => {
            event.preventDefault();
        });

        const mappings = createMappingsFromMappingData(
            formMapperAppData.formMapper,
        );

        const serializedForm = d365mktformcapture.serializeForm(
            dynamicsForm,
            mappings,
        );
        const payload = serializedForm.SerializedForm.build();

        const captureConfig = {
            FormId: formMapperAppData.dynamicsId,
            FormApiUrl: DYNAMICS_FORM_API_URL ?? "",
        };

        // The await is necessary to ensure the form is submitted
        // or the error is thrown
        return await d365mktformcapture.submitForm(captureConfig, payload);
    } catch (error) {
        throw new Error(`Dynamics form submission error: ${error}`);
    }
};
